import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Stack,
  Typography,
  Button,
  Card,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Drawer,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Chip,
  InputAdornment,
} from '@mui/material';
import { History, X as CloseIcon } from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  getServiceProviders,
  syncServiceProviderBalances,
  deleteServiceProvider,
  adjustServiceProviderBalance,
} from '../api/services';
import { hasPermission, isAdminOrOwner, PERMISSIONS } from '../utils/hasPermission';

const getProviderModeShortLabel = (p) => {
  if (!p) return 'Live API Synced';
  if (p.supportsBalanceCheck === false || p.balanceMode === 'manual_ledger') {
    return 'Manual Virtual Ledger';
  }
  if (p.balanceMode === 'unlimited') {
    return 'Unlimited Mode';
  }
  return 'Live API Synced';
};

const renderStatusBadge = (row, balanceNum, isLow) => {
  const isManual = row.supportsBalanceCheck === false || row.balanceMode === 'manual_ledger';
  const isUnlimited = row.balanceMode === 'unlimited';

  let statusPill = null;
  if (!row.isActive) {
    statusPill = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: 1.5,
          py: 0.5,
          borderRadius: '16px',
          bgcolor: '#F3F4F6',
          color: '#6B7280',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#6B7280' }} />
        <span>Inactive</span>
      </Box>
    );
  } else if (isUnlimited) {
    statusPill = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: 1.5,
          py: 0.5,
          borderRadius: '16px',
          bgcolor: '#EFF6FF',
          color: '#1D4ED8',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2563EB' }} />
        <span>Unlimited</span>
      </Box>
    );
  } else if (balanceNum <= 0) {
    statusPill = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: 1.5,
          py: 0.5,
          borderRadius: '16px',
          bgcolor: '#FEE2E2',
          color: '#DC2626',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#DC2626' }} />
        <span>Empty</span>
      </Box>
    );
  } else if (isLow) {
    statusPill = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: 1.5,
          py: 0.5,
          borderRadius: '16px',
          bgcolor: '#FEF3C7',
          color: '#D97706',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#D97706' }} />
        <span>Low balance</span>
      </Box>
    );
  } else {
    statusPill = (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          px: 1.5,
          py: 0.5,
          borderRadius: '16px',
          bgcolor: '#DCFCE7',
          color: '#16A34A',
          fontSize: '0.8rem',
          fontWeight: 600,
        }}
      >
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16A34A' }} />
        <span>Healthy</span>
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {statusPill}
      {isManual && (
        <Chip
          label="Manual Ledger"
          size="small"
          sx={{
            height: 22,
            fontSize: '0.72rem',
            fontWeight: 700,
            bgcolor: '#F3E8FF',
            color: '#7E22CE',
            border: '1px solid #E9D5FF',
          }}
        />
      )}
    </Stack>
  );
};

const formatSyncedTime = (dateStr) => {
  if (!dateStr) return '10:08';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '10:08';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};

export default function ServiceProvidersPage() {
  const navigate = useNavigate();

  const { user, activeApp } = useSelector((state) => state.auth);
  const isPrivileged = isAdminOrOwner(user, activeApp);
  const canManage =
    isPrivileged ||
    hasPermission(user, [
      PERMISSIONS.SERVICES_WRITE,
      PERMISSIONS.PROCESSORS_MANAGE,
    ]);

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  // Delete Dialog State
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Quick Top-up Modal State
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [providerToAdjust, setProviderToAdjust] = useState(null);
  const [adjustAction, setAdjustAction] = useState('add');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  const fetchProviders = async () => {
    if (!isPrivileged) return;
    setLoading(true);
    try {
      const res = await getServiceProviders();
      if (res.ok && res.data?.data) {
        setProviders(res.data.data);
      } else {
        toast.error('Failed to load service providers.');
      }
    } catch (err) {
      toast.error('Error fetching service providers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isPrivileged) return;
    fetchProviders();
  }, [isPrivileged]);

  const handleSyncBalances = async () => {
    if (!canManage) {
      toast.error('You do not have permission to sync provider balances.');
      return;
    }
    setSyncing(true);
    try {
      const res = await syncServiceProviderBalances();
      if (res.ok && res.data?.data) {
        setProviders(res.data.data);
        toast.success('Live provider wallet balances synced successfully!');
      } else {
        toast.error('Failed to sync provider balances.');
      }
    } catch (err) {
      toast.error('Error syncing provider balances.');
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!providerToDelete) return;
    if (!canManage) {
      toast.error('You do not have permission to delete service providers.');
      return;
    }
    setDeleting(true);
    try {
      const res = await deleteServiceProvider(providerToDelete.id);
      if (res.ok) {
        toast.success(`Deleted vendor provider ${providerToDelete.name} successfully!`);
        setProviderToDelete(null);
        fetchProviders();
      } else {
        toast.error(res.data?.message || 'Failed to delete service provider.');
      }
    } catch (err) {
      toast.error('Error deleting service provider.');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmAdjustment = async () => {
    if (!providerToAdjust || !adjustAmount) return;
    if (!canManage) {
      toast.error('You do not have permission to adjust provider balances.');
      return;
    }
    const num = Number(adjustAmount);
    if (Number.isNaN(num) || num <= 0) {
      toast.error('Please enter a valid positive amount.');
      return;
    }

    setAdjusting(true);
    try {
      const res = await adjustServiceProviderBalance(providerToAdjust.id, {
        action: adjustAction,
        amount: num,
        notes: adjustNotes.trim() || undefined,
      });

      if (res.ok) {
        toast.success(
          res.data?.message ||
            `Successfully ${adjustAction === 'add' ? 'topped up' : 'updated'} balance for ${providerToAdjust.name}!`
        );
        setTopUpModalOpen(false);
        setProviderToAdjust(null);
        setAdjustAmount('');
        setAdjustNotes('');
        await fetchProviders();
      } else {
        toast.error(res.data?.message || 'Failed to adjust balance.');
      }
    } catch (err) {
      toast.error('Error updating provider balance.');
    } finally {
      setAdjusting(false);
    }
  };

  // Calculated Metrics
  const totalWalletBalance = providers.reduce((acc, p) => acc + Number(p.balance || 0), 0);
  const needFundingCount = providers.filter((p) => {
    const bal = Number(p.balance || 0);
    const limit = Number(p.lowBalanceThreshold || 5000);
    return bal <= 0 || bal < limit;
  }).length;

  const renderTableRows = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
            <CircularProgress size={32} />
          </TableCell>
        </TableRow>
      );
    }

    if (providers.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
            <Typography variant="body2" color="text.secondary">
              No service providers configured yet.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return providers.map((row) => {
      const balanceNum = Number(row.balance || 0);
      const thresholdNum = Number(row.lowBalanceThreshold || 5000);
      const isLow = balanceNum < thresholdNum;
      const initial = (row.name || 'P').trim().charAt(0).toUpperCase();

      return (
        <TableRow
          key={row.id}
          hover
          sx={{
            '& td': {
              borderBottom: '1px solid #F3F4F6',
              py: 2.25,
            },
          }}
        >
          {/* Provider column with dark square avatar */}
          <TableCell sx={{ pl: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  bgcolor: '#22252A',
                  color: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  flexShrink: 0,
                }}
              >
                {initial}
              </Box>
              <div>
                <Typography sx={{ fontWeight: 700, fontSize: '0.925rem', color: '#111827', lineHeight: 1.3 }}>
                  {row.name}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  {row.slug}
                </Typography>
              </div>
            </Stack>
          </TableCell>

          {/* Balance */}
          <TableCell>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.925rem',
                color: balanceNum <= 0 ? '#DC2626' : '#16A34A',
              }}
            >
              ₦{balanceNum.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </TableCell>

          {/* Low limit */}
          <TableCell>
            <Typography sx={{ color: '#111827', fontWeight: 500, fontSize: '0.875rem' }}>
              ₦{thresholdNum.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </TableCell>

          {/* Status badge pill */}
          <TableCell>
            {renderStatusBadge(row, balanceNum, isLow)}
          </TableCell>

          {/* Total spent */}
          <TableCell>
            <Typography sx={{ color: '#111827', fontWeight: 500, fontSize: '0.875rem' }}>
              ₦{Number(row.totalSpent || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </TableCell>

          {/* Synced time */}
          <TableCell>
            <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>
              {formatSyncedTime(row.lastBalanceCheckAt || row.updatedAt)}
            </Typography>
          </TableCell>

          {/* Text Actions: Top Up / View / Delete */}
          <TableCell align="right" sx={{ pr: 3 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
              {canManage && (
                <Typography
                  component="button"
                  onClick={() => {
                    setProviderToAdjust(row);
                    setAdjustAction('add');
                    setAdjustAmount('');
                    setAdjustNotes('');
                    setTopUpModalOpen(true);
                  }}
                  sx={{
                    background: 'none',
                    border: 'none',
                    p: 0,
                    color: '#059669',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Top Up
                </Typography>
              )}
              <Typography
                component="button"
                onClick={() => navigate(`/dashboard/service-providers/details/${row.id}`)}
                sx={{
                  background: 'none',
                  border: 'none',
                  p: 0,
                  color: '#0066FE',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                View
              </Typography>
              {canManage && (
                <Typography
                  component="button"
                  onClick={() => setProviderToDelete(row)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    p: 0,
                    color: '#DC2626',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Delete
                </Typography>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      );
    });
  };

  if (!isPrivileged) {
    return (
      <Page title="Service Providers | Access Denied">
        <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
          <Card variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" color="error" fontWeight={800} sx={{ mb: 1.5 }}>
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Service provider balances, API credentials, and vendor processor settings are restricted to workspace owners and administrators only.
            </Typography>
            <Button
              onClick={() => navigate('/dashboard/services')}
              variant="contained"
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
            >
              Go to Services
            </Button>
          </Card>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Service Providers | Dashboard">
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Top Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={4}
        >
          <div>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#111827',
                fontSize: { xs: '1.75rem', sm: '2.25rem' },
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}
            >
              Service providers
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.925rem' }}>
              Vendor accounts, wallet balances and health for airtime, data and electricity.
            </Typography>
          </div>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              startIcon={<History size={16} />}
              onClick={() => navigate('/dashboard/audit-logs')}
              sx={{
                borderRadius: '24px',
                borderColor: '#E5E7EB',
                color: '#374151',
                px: 2.5,
                py: 0.9,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: '#D1D5DB',
                  bgcolor: '#F9FAFB',
                },
              }}
            >
              Audit Trail
            </Button>
            {canManage && (
              <Button
                onClick={handleSyncBalances}
                disabled={syncing}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#EAECEF',
                  color: '#1F2937',
                  px: 2.75,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#DFE2E6',
                    boxShadow: 'none',
                  },
                }}
              >
                {syncing ? 'Syncing...' : 'Sync balances'}
              </Button>
            )}
            {canManage && (
              <Button
                onClick={() => navigate('/dashboard/service-providers/add')}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#0066FE',
                  color: '#FFFFFF',
                  px: 2.75,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#0052CC',
                    boxShadow: 'none',
                  },
                }}
              >
                Add provider
              </Button>
            )}
          </Stack>
        </Stack>

        {/* 3 Metric Cards */}
        <Grid container spacing={3} mb={4}>
          {/* Providers count */}
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: '20px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
              }}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
                Providers
              </Typography>
              <Typography
                sx={{
                  fontSize: '2.25rem',
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                {providers.length}
              </Typography>
            </Card>
          </Grid>

          {/* Total wallet balance */}
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: '20px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
              }}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
                Total wallet balance
              </Typography>
              <Typography
                sx={{
                  fontSize: '2.25rem',
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                ₦{totalWalletBalance.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Card>
          </Grid>

          {/* Need funding */}
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 3,
                borderRadius: '20px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
              }}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 1 }}>
                Need funding
              </Typography>
              <Typography
                sx={{
                  fontSize: '2.25rem',
                  fontWeight: 800,
                  color: needFundingCount > 0 ? '#DC2626' : '#111827',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                {needFundingCount}
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Table Container */}
        <Card
          sx={{
            borderRadius: '20px',
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
            border: '1px solid #ECEEF1',
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      borderBottom: '1px solid #F0F2F5',
                      color: '#6B7280',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      py: 2,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  <TableCell sx={{ pl: 3 }}>Provider</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Low limit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total spent</TableCell>
                  <TableCell>Synced</TableCell>
                  <TableCell align="right" sx={{ pr: 3 }}>{/* Actions header */}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderTableRows()}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={Boolean(providerToDelete)} onClose={() => setProviderToDelete(null)}>
          <DialogTitle>Delete Service Provider Account</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete vendor provider account{' '}
              <strong>"{providerToDelete?.name}"</strong> ({providerToDelete?.slug})? This action cannot be undone and will remove it from any configured failover routes.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ pb: 2, px: 3 }}>
            <Button onClick={() => setProviderToDelete(null)} disabled={deleting} color="inherit">
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
              {deleting ? 'Deleting...' : 'Delete Provider'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Quick Top Up / Adjust Balance Drawer (Side Modal) */}
        <Drawer
          anchor="right"
          open={topUpModalOpen && Boolean(providerToAdjust)}
          onClose={() => !adjusting && setTopUpModalOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 480, md: 540 },
              maxWidth: '100vw',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.12)',
              borderLeft: (t) => `1px solid ${t.palette.divider}`,
            },
          }}
        >
          {/* Drawer Header */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              borderBottom: '1px solid #F3F4F6',
              bgcolor: '#FFFFFF',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
              <div>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
                  Adjust Wallet Balance
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                  Update or top up balance for <strong>{providerToAdjust?.name}</strong> ({providerToAdjust?.slug}).
                </Typography>
              </div>
              <IconButton onClick={() => setTopUpModalOpen(false)} size="small" sx={{ bgcolor: '#F9FAFB' }}>
                <CloseIcon size={18} />
              </IconButton>
            </Stack>
          </Box>

          {/* Drawer Body */}
          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            {providerToAdjust && (
              <Box
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: '14px',
                  bgcolor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <div>
                    <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
                      CURRENT RECORDED BALANCE
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
                      ₦{Number(providerToAdjust.balance || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                    </Typography>
                  </div>
                  <Chip
                    label={getProviderModeShortLabel(providerToAdjust)}
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: '#EFF6FF', color: '#1D4ED8' }}
                  />
                </Stack>
              </Box>
            )}

            {/* Action Selection */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>
              Adjustment Action
            </Typography>
            <RadioGroup
              value={adjustAction}
              onChange={(e) => setAdjustAction(e.target.value)}
              sx={{ mb: 2.5 }}
            >
              <FormControlLabel
                value="add"
                control={<Radio sx={{ color: '#0066FE', '&.Mui-checked': { color: '#0066FE' } }} />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Add to Balance (Top-up)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      e.g. "I've added ₦40,000" (Increments the current balance)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1, width: '100%' }}
              />
              <FormControlLabel
                value="set"
                control={<Radio sx={{ color: '#0066FE', '&.Mui-checked': { color: '#0066FE' } }} />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Set Exact Balance
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      Directly overrides the current balance
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%' }}
              />
            </RadioGroup>

            {/* Amount Field */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>
              Amount (₦)
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="e.g. 40000"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700 }}>₦</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />

            {/* Quick Preset Buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
              {[10000, 20000, 40000, 50000, 100000].map((preset) => (
                <Chip
                  key={preset}
                  label={`+₦${preset.toLocaleString()}`}
                  onClick={() => {
                    const currentBal = Number(providerToAdjust?.balance || 0);
                    if (adjustAction === 'add') {
                      setAdjustAmount(preset);
                    } else {
                      setAdjustAmount(currentBal + preset);
                    }
                  }}
                  clickable
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    border: '1px solid #E5E7EB',
                    bgcolor: '#FFFFFF',
                    '&:hover': { bgcolor: '#F3F4F6' },
                  }}
                />
              ))}
            </Stack>

            {/* Calculated Preview */}
            {adjustAmount && !Number.isNaN(Number(adjustAmount)) && Number(adjustAmount) > 0 && providerToAdjust && (
              <Box
                sx={{
                  p: 2,
                  mb: 2.5,
                  borderRadius: '12px',
                  bgcolor: '#ECFDF5',
                  border: '1px solid #A7F3D0',
                }}
              >
                <Typography variant="body2" sx={{ color: '#065F46', fontWeight: 600 }}>
                  New Balance will be:{' '}
                  <strong style={{ fontSize: '1.05rem' }}>
                    ₦
                    {(adjustAction === 'add'
                      ? Number(providerToAdjust.balance || 0) + Number(adjustAmount)
                      : Number(adjustAmount)
                    ).toLocaleString('en-NG', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </strong>
                </Typography>
              </Box>
            )}

            {/* Audit Notes */}
            <TextField
              fullWidth
              label="Audit Reason / Notes (Optional)"
              placeholder="e.g. Added ₦40,000 via provider bank transfer"
              value={adjustNotes}
              onChange={(e) => setAdjustNotes(e.target.value)}
              helperText="Recorded permanently in tenant audit logs"
            />
          </Box>

          {/* Drawer Footer */}
          <Box
            sx={{
              p: 2.5,
              px: 3,
              borderTop: '1px solid #F3F4F6',
              bgcolor: '#FFFFFF',
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            <Button
              onClick={() => setTopUpModalOpen(false)}
              disabled={adjusting}
              sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmAdjustment}
              disabled={adjusting || !adjustAmount || Number(adjustAmount) <= 0}
              sx={{
                borderRadius: '12px',
                bgcolor: '#0066FE',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#0052CC' },
              }}
            >
              {(() => {
                if (adjusting) return 'Saving...';
                if (adjustAction === 'add') return 'Confirm Top-Up';
                return 'Set Balance';
              })()}
            </Button>
          </Box>
        </Drawer>
      </Container>
    </Page>
  );
}
